import type {CursorRequest, CursorResponse} from '@/client/mentor/types'
import {PaginationDirection} from '@/client/mentor/types'
import {defaultApi} from '@/axios'
import {isNotNullOrUndefined} from '@/utils/utils'
import {safeParseIsoDate} from '@/utils/parse'

export {PaginationDirection} from '@/client/mentor/types'

export enum AgentType {
  CHAT_AGENT = 'CHAT_AGENT',
  SKILL_AGENT = 'SKILL_AGENT',
}

export enum ChartType {
  LINE = 'LINE',
  BAR = 'BAR',
  PIE = 'PIE',
}

export enum ConversationMessageType {
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
  SYSTEM = 'SYSTEM',
}

export enum ResponseFormat {
  TEXT = 'TEXT',
  TABLE = 'TABLE',
  CHART = 'CHART',
}

export enum AssistantMessageStatus {
  PENDING = 'PENDING',
  ANSWERED = 'ANSWERED',
  NEEDS_INPUT = 'NEEDS_INPUT',
  OUT_OF_SCOPE = 'OUT_OF_SCOPE',
  FAILED = 'FAILED',
  TIMED_OUT = 'TIMED_OUT',
}

export enum MessageProgressLifecycle {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export enum MessageProgressPhase {
  CREATE_MESSAGE = 'CREATE_MESSAGE',
  SCHEDULE_MESSAGE = 'SCHEDULE_MESSAGE',
  CALLING_AGENT = 'CALLING_AGENT',
  PROCESS_TOOL = 'PROCESS_TOOL',
  SYNTHESIZING_RESPONSE = 'SYNTHESIZING_RESPONSE',
}

export enum ToolCallStatus {
  RUNNING = 'RUNNING',
  FINISHED = 'FINISHED',
}

export interface MessageProgressFromApi {
  lifecycle: MessageProgressLifecycle
  phase?: MessageProgressPhase | null
  toolName?: string | null
  toolArguments?: string | null
  toolCallStatus?: ToolCallStatus | null
  createdAt: string
  updatedAt?: string | null
}

export interface MessageProgress extends Omit<MessageProgressFromApi, 'createdAt' | 'updatedAt'> {
  createdAt: Date
  updatedAt?: Date | null
}

export interface DataPoint {
  x: unknown
  y: number | null
}

export interface ChartSeries {
  name: string
  unit?: string | null
  points: DataPoint[]
}

export interface ChartData {
  type: ChartType
  xLabel: string
  yLabel: string
  series: ChartSeries[]
}

export enum Highlight {
  POS = 'POS',
  NEG = 'NEG',
  DIM = 'DIM',
  BLD = 'BLD',
  ITL = 'ITL',
}

export interface Cell {
  v: unknown
  s?: Highlight | null
}

export interface TableData {
  cols: string[]
  rows: Cell[][]
  unit?: Record<string, string>
}

export interface KnowledgeSource {
  url: string
  title?: string | null
}

export interface InvokedTool {
  toolName: string
  arguments: string
  result?: string | null
}

export interface UsedTokens {
  input: number
  output: number
  cacheRead?: number | null
  cacheWrite?: number | null
}

export interface MessageFeedback {
  rating: MessageFeedbackRating
  message: string | null
}

export interface MessageMeta {
  agent?: AgentType | null
  invokedTools?: InvokedTool[] | null
  usedTokens?: UsedTokens | null
  feedback?: MessageFeedback | null
}

interface BaseMessageFromApi {
  id: number
  conversationId: string
  createdAt: string
  meta?: MessageMeta | null
  progress?: MessageProgressFromApi[] | null
}

interface BaseMessage extends Omit<BaseMessageFromApi, 'createdAt' | 'progress'> {
  createdAt: Date
  progress?: MessageProgress[] | null
}

export interface UserMessageContent {
  type: ConversationMessageType.USER
  prompt: string
  preferredFormat?: ResponseFormat | null
}

export interface UserMessageFromApi extends BaseMessageFromApi {
  type: ConversationMessageType.USER
  content: UserMessageContent
}

export interface UserMessage extends BaseMessage {
  type: ConversationMessageType.USER
  content: UserMessageContent
}

export interface AssistantMessageContent {
  type: ConversationMessageType.ASSISTANT
  status: AssistantMessageStatus
  format: ResponseFormat
  answer: string
  tableData?: TableData | null
  chartData?: ChartData | null
  sources?: KnowledgeSource[] | null
  suggestions?: string[] | null
}

export interface AssistantMessageFromApi extends BaseMessageFromApi {
  type: ConversationMessageType.ASSISTANT
  content: AssistantMessageContent
}

export interface AssistantMessage extends BaseMessage {
  type: ConversationMessageType.ASSISTANT
  content: AssistantMessageContent
}

export interface SystemMessageContent {
  type: ConversationMessageType.SYSTEM
  errorCode: string
  reason?: string | null
}

export interface SystemMessageFromApi extends BaseMessageFromApi {
  type: ConversationMessageType.SYSTEM
  content: SystemMessageContent
}

export interface SystemMessage extends BaseMessage {
  type: ConversationMessageType.SYSTEM
  content: SystemMessageContent
}

export type MessageFromApi = UserMessageFromApi | AssistantMessageFromApi | SystemMessageFromApi
export type Message = UserMessage | AssistantMessage | SystemMessage

export enum MessageFeedbackRating {
  THUMBS_UP = 'THUMBS_UP',
  THUMBS_DOWN = 'THUMBS_DOWN',
}

export interface MessageFeedbackRequest {
  companyId: number
  rating: MessageFeedbackRating
  message?: string | null
}

export class MessagesClient {
  async saveMessageFeedback(conversationId: string, messageId: number, request: MessageFeedbackRequest): Promise<Message> {
    return defaultApi
      .put<MessageFromApi>(`/api/mentor/conversations/${conversationId}/messages/${messageId}/feedback`, request)
      .then((res) => parseMessage(res.data))
  }

  async getBatchMessages(companyId: number, messageIds: number[]): Promise<Message[]> {
    return defaultApi
      .get<MessageFromApi[]>(`/api/mentor/conversations/batch/messages`, {
        params: {companyId, messageIds},
      })
      .then((res) => res.data.map(parseMessage))
  }

  async getMessage(companyId: number, conversationId: string, messageId: number): Promise<Message> {
    return defaultApi
      .get<MessageFromApi>(`/api/mentor/conversations/${conversationId}/messages/${messageId}`, {
        params: {companyId},
      })
      .then((res) => parseMessage(res.data))
  }

  async getMessages(
    companyId: number,
    conversationId: string,
    request: CursorRequest<number> = {}
  ): Promise<CursorResponse<Message, number>> {
    const {cursor = undefined, limit = undefined, direction = PaginationDirection.DOWN} = request

    return defaultApi
      .get<CursorResponse<MessageFromApi, number>>(`/api/mentor/conversations/${conversationId}/messages`, {
        params: {companyId, cursor, limit, direction},
      })
      .then((res) => ({...res.data, items: res.data.items.map(parseMessage)}))
  }
}

export function parseMessage(message: MessageFromApi): Message {
  return {
    ...message,
    createdAt: safeParseIsoDate(message.createdAt),
    progress: message.progress ? message.progress.map(parseMessageProgress) : message.progress,
  }
}

function parseMessageProgress(progress: MessageProgressFromApi): MessageProgress {
  return {
    ...progress,
    createdAt: safeParseIsoDate(progress.createdAt),
    updatedAt: isNotNullOrUndefined(progress.updatedAt) ? safeParseIsoDate(progress.updatedAt) : progress.updatedAt,
  }
}

const messagesClient = new MessagesClient()

export default messagesClient
